import { useState, type KeyboardEvent } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChartContainer, type ChartConfig } from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { useCompanyActualVsBudget } from '@/hooks/use-companies'
import {
  getActualVsBudgetStateMessage,
  type ActualVsBudgetComparison,
  type CompanyActualVsBudgetResponse,
} from '@/features/company-overview/actual-vs-budget'
import { formatCurrency, formatDate } from '@/lib/format'

const chartConfig = {
  actual: {
    label: 'Actual',
    color: 'var(--primary)',
  },
  budget: {
    label: 'Budget',
    color: 'var(--secondary)',
  },
} satisfies ChartConfig

type ActualVsBudgetChartDatum = {
  period: string
  actual: number | null
  budget: number | null
}

function formatMonth(periodEnd: string | null): string {
  if (!periodEnd) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(periodEnd))
}

function toChartData(
  points: ActualVsBudgetComparison[]
): ActualVsBudgetChartDatum[] {
  return points.map((point) => ({
    period: formatMonth(point.periodEnd),
    actual: point.actual,
    budget: point.budget,
  }))
}

function formatInspectionReadout(datum: ActualVsBudgetChartDatum | undefined) {
  if (!datum) return 'No month is available for inspection'
  return `${datum.period} · Actual ${formatCurrency(datum.actual)} · Budget ${formatCurrency(datum.budget)}`
}

function StateMessage({
  comparison,
}: {
  comparison: ActualVsBudgetComparison
}) {
  const message = getActualVsBudgetStateMessage(comparison)
  if (!message) return null

  return (
    <div
      role="status"
      className="rounded-sm border border-amber-700/20 bg-amber-50 px-2 py-1.5"
    >
      <Typography variant="body-sm" className="text-amber-900">
        {message}
      </Typography>
    </div>
  )
}

function ScenarioLabel({
  label,
  value,
  colorClassName,
}: {
  label: 'Actual' | 'Budget'
  value: number | null
  colorClassName: string
}) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        aria-hidden="true"
        className={`h-0.5 w-4 rounded-full ${colorClassName}`}
      />
      <Typography as="span" variant="body-sm" className="text-primary/70">
        {label}
      </Typography>
      <Typography
        as="span"
        variant="body-sm"
        className="font-medium tabular-nums text-primary"
      >
        {formatCurrency(value)}
      </Typography>
    </div>
  )
}

export interface ActualVsBudgetCardProps {
  companyId?: string
  data?: CompanyActualVsBudgetResponse
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
  showFinancialsLink?: boolean
}

export function ActualVsBudgetCard({
  companyId,
  data,
  isLoading = false,
  isError = false,
  onRetry,
  showFinancialsLink = false,
}: ActualVsBudgetCardProps) {
  const [activeTrendIndex, setActiveTrendIndex] = useState(0)
  const financialsLink =
    showFinancialsLink && companyId ? (
      <Button asChild variant="outline" size="sm" className="mt-3 w-full">
        <Link
          to="/companies/$companyId"
          params={{ companyId }}
          search={{ tab: 'financials' }}
          hash="actual-vs-budget"
        >
          View in Financials
        </Link>
      </Button>
    ) : null

  if (isLoading) {
    return (
      <article aria-label="Actual vs Budget" className="flex flex-col">
        <Skeleton className="h-[248px] w-full rounded-sm" />
        <Typography variant="body-sm" className="sr-only">
          Loading Actual vs Budget
        </Typography>
        {financialsLink}
      </article>
    )
  }

  if (isError) {
    return (
      <article
        aria-label="Actual vs Budget"
        className="flex min-h-62 flex-col items-center justify-center gap-3 rounded-sm border border-border bg-white p-4 text-center"
      >
        <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
        <Typography variant="body-sm" role="alert">
          Actual vs Budget could not be loaded.
        </Typography>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {financialsLink}
      </article>
    )
  }

  const comparison = data?.comparisons.find(
    (item) => item.requestedMetric === 'Revenue'
  )
  const trend = data?.trends.find((item) => item.requestedMetric === 'Revenue')

  if (!data || !comparison) {
    return (
      <article
        aria-label="Actual vs Budget"
        className="flex min-h-62 flex-col items-center justify-center rounded-sm border border-border bg-white p-4 text-center"
      >
        <Typography variant="body-sm" role="status" className="text-primary/70">
          Actual vs Budget data is not available.
        </Typography>
        {financialsLink}
      </article>
    )
  }

  const chartData = toChartData(trend?.points ?? [])
  const coverage = data.budgetCoverage
  const boundedTrendIndex = Math.min(
    activeTrendIndex,
    Math.max(chartData.length - 1, 0)
  )
  const inspectionReadout = formatInspectionReadout(
    chartData[boundedTrendIndex]
  )

  function handleInspectionKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (chartData.length === 0) return
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      setActiveTrendIndex((current) =>
        current <= 0 ? chartData.length - 1 : current - 1
      )
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      setActiveTrendIndex((current) => (current + 1) % chartData.length)
    }
  }

  return (
    <article
      aria-label="Revenue Actual vs Budget"
      data-state={comparison.state}
      className="min-h-62 rounded-sm border border-border bg-white p-4 transition-[box-shadow,border-color] duration-200 ease-out hover:border-primary"
    >
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <Typography variant="body" className="font-normal text-primary">
            Revenue: Actual vs Budget
          </Typography>
          <Typography variant="body-sm" className="mt-0.5 text-primary/70">
            {data.fiscalContext?.fiscalYearLabel ?? 'Fiscal year unavailable'}
            {' · '}
            FYTD
          </Typography>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {data.funds.map((fund) => (
            <Badge
              key={fund.id}
              title={fund.displayName}
              className="rounded-sm"
            >
              {fund.abbreviation}
            </Badge>
          ))}
        </div>
      </div>

      <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1">
        <ScenarioLabel
          label="Actual"
          value={comparison.actual}
          colorClassName="bg-primary"
        />
        <ScenarioLabel
          label="Budget"
          value={comparison.budget}
          colorClassName="bg-secondary"
        />
      </div>

      <StateMessage comparison={comparison} />

      {chartData.length > 0 ? (
        <>
          <div
            tabIndex={0}
            onKeyDown={handleInspectionKeyDown}
            aria-label={`Actual vs Budget inspection. ${inspectionReadout}. Use Left and Right arrow keys to inspect months.`}
            className="mt-2 flex min-h-6 items-center rounded-sm bg-primary/[0.04] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Typography
              as="span"
              variant="body-sm"
              className="truncate tabular-nums text-primary/75"
            >
              {inspectionReadout}
            </Typography>
          </div>
          <ChartContainer
            config={chartConfig}
            className="h-24 w-full"
            role="img"
            aria-label="Monthly Revenue Actual and Budget lines"
          >
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
              onMouseMove={(nextState) => {
                const nextIndex = Number(nextState.activeTooltipIndex)
                if (
                  Number.isInteger(nextIndex) &&
                  nextIndex >= 0 &&
                  nextIndex < chartData.length
                ) {
                  setActiveTrendIndex(nextIndex)
                }
              }}
            >
              <CartesianGrid vertical={false} stroke="var(--border)" />
              <XAxis
                dataKey="period"
                tickLine={false}
                axisLine={false}
                tickMargin={6}
                minTickGap={14}
              />
              <YAxis hide domain={['auto', 'auto']} />
              <Line
                dataKey="actual"
                name="Actual"
                type="monotone"
                stroke="var(--color-actual)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="budget"
                name="Budget"
                type="monotone"
                stroke="var(--color-budget)"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ChartContainer>
        </>
      ) : (
        <div className="flex h-24 items-center justify-center">
          <Typography
            variant="body-sm"
            role="status"
            className="text-primary/70"
          >
            No monthly trend is available.
          </Typography>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-primary/70">
        <div className="flex items-center gap-1">
          <RefreshCw className="h-3 w-3" aria-hidden="true" />
          <Typography as="span" variant="body-sm">
            Actual closed {formatDate(data.latestClosedActualPeriod)}
          </Typography>
        </div>
        <Typography as="span" variant="body-sm">
          Budget coverage:{' '}
          {coverage.available
            ? `${formatDate(coverage.firstPeriod)}–${formatDate(coverage.latestPeriod)}`
            : 'Not available'}
        </Typography>
      </div>

      {financialsLink}
    </article>
  )
}

export function ActualVsBudgetCardContainer({
  companyId,
}: {
  companyId: string
}) {
  const query = useCompanyActualVsBudget(companyId, true)

  return (
    <ActualVsBudgetCard
      companyId={companyId}
      data={query.data}
      isLoading={query.isLoading}
      isError={query.isError}
      showFinancialsLink
      onRetry={() => {
        void query.refetch()
      }}
    />
  )
}
